import axiosInstance from 'src/utils/axios';
import type {
  ApplyResult,
  CrossMemberDocument,
  DocCategoryWithSubTypes,
  DocSchemaStep,
  DocSubType,
  ExtractionMatch,
  FileListResponse,
  LinkedSurveyFileItem,
  RenterFileDetail,
  RenterFileListItem,
  RenterFileStatus,
  ReusedSurveyFile,
  SummaryResponse,
  SurveyResolveResponse,
} from 'src/types/aiFileUploadV2';

function devWarn(context: string, msg: string): void {
  if (process.env.NODE_ENV !== 'production') {
    console.warn(`[aiFileUploadV2:${context}] ${msg}`);
  }
}

// Base prefix — axiosInstance baseURL is /api/, so we need v2/renter-hub/
const BASE = 'v2/renter-hub';

export async function resolveSurvey({
  certificationId,
  userId,
  signal,
}: {
  certificationId?: number;
  userId?: number;
  signal?: AbortSignal;
}): Promise<SurveyResolveResponse> {
  const params: Record<string, number> = {};
  if (certificationId !== undefined) {
    params.certification_id = certificationId;
  }
  if (userId !== undefined) {
    params.user_id = userId;
  }
  const res = await axiosInstance.get<SurveyResolveResponse>(
    `${BASE}/survey/resolve/`,
    { params: Object.keys(params).length > 0 ? params : undefined, signal }
  );
  if (!res.data.survey_id) {
    devWarn('resolveSurvey', 'Response missing survey_id');
  }
  return res.data;
}

export async function listAIFiles(
  surveyId: number,
  signal?: AbortSignal,
  userId?: number,
  targetAnswerGroupId?: number | null,
  targetQitId?: number | null
): Promise<FileListResponse> {
  const params: Record<string, number> = { survey: surveyId };
  if (userId !== undefined) {
    params.user_id = userId;
  }
  if (targetAnswerGroupId != null) {
    params.target_answer_group = targetAnswerGroupId;
  }
  if (targetQitId != null) {
    params.target_qit = targetQitId;
  }
  const res = await axiosInstance.get<FileListResponse>(`${BASE}/uploads/`, {
    params,
    signal,
  });
  if (!res.data.meta) {
    devWarn('listAIFiles', 'Response missing meta field');
  }
  return res.data;
}

export async function getAIFileDetail(
  fileId: number,
  signal?: AbortSignal
): Promise<RenterFileDetail> {
  const res = await axiosInstance.get<RenterFileDetail>(
    `${BASE}/uploads/${fileId}/`,
    { signal }
  );
  if (!res.data.steps) {
    devWarn('getAIFileDetail', 'Response missing steps field');
  }
  return res.data;
}

/**
 * Lightweight images of an upload (one per page), for "See what I uploaded".
 * Returns presigned URLs to cached page images (never the original file).
 */
export async function getRenterFilePreview(
  fileId: number,
  signal?: AbortSignal
): Promise<{ urls: string[] }> {
  const res = await axiosInstance.get<{ urls: string[] }>(
    `${BASE}/uploads/${fileId}/preview/`,
    { signal }
  );
  return res.data;
}

export interface UploadAIFileResult {
  id: number;
  uuid: string;
  filename: string;
  status: RenterFileStatus;
  target_answer_group_id: number | null;
  /** Deploy-window echo of linked/processing/existing/already-on-file rows.
   *  Old bundles read `created`; the current client ignores these. */
  compat_echo?: boolean;
}

/**
 * Outcome of a single upload attempt (ENG-3331). A blocked re-upload creates no
 * new file — it appears in `blocked` instead of `created`. Both are arrays so a
 * multi-file response never silently drops entries.
 */
export interface UploadAIFileOutcome {
  created: UploadAIFileResult[];
  blocked: RenterFileListItem[];
  processing: RenterFileListItem[];
  existing: RenterFileListItem[];
  linked: LinkedSurveyFileItem[];
  alreadyOnFile: OnFileDocument[];
  /** ENG-4086: copies already on another member's survey of this cert.
   *  Optional: older server shapes omit the key entirely. */
  crossMember?: CrossMemberDocument[];
}

export interface OnFileDocument {
  survey_file_id: number;
  renter_file_id: number | null;
  filename: string | null;
  question: string | null;
  uploaded_at: string;
  doc_category_key: string | null;
  /** ENG-4064: identity of the copy already on the question, so the interview
   *  embed can highlight (and later open) that exact tile. */
  survey_answer_id?: number | null;
  answer_group_id?: number | null;
}

export async function uploadAIFile({
  surveyId,
  certificationId,
  targetQitId,
  targetAnswerGroupId,
  questionGroupId,
  file,
  onProgress,
  signal,
}: {
  surveyId: number;
  certificationId?: number;
  targetQitId?: number;
  targetAnswerGroupId?: number;
  questionGroupId?: number;
  file: File;
  onProgress?: (pct: number) => void;
  signal?: AbortSignal;
}): Promise<UploadAIFileOutcome> {
  const formData = new FormData();
  // T1-2: field name is 'files' not 'files[]'
  formData.append('files', file);
  formData.append('survey_id', String(surveyId));
  if (certificationId !== undefined) {
    formData.append('certification_id', String(certificationId));
  }
  if (targetQitId !== undefined) {
    formData.append('target_qit_id', String(targetQitId));
  }
  if (targetAnswerGroupId !== undefined) {
    formData.append('target_answer_group_id', String(targetAnswerGroupId));
  }
  if (questionGroupId !== undefined) {
    formData.append('question_group_id', String(questionGroupId));
  }

  const res = await axiosInstance.post<{
    created: UploadAIFileResult[];
    errors: unknown[];
    blocked?: RenterFileListItem[];
    processing?: RenterFileListItem[];
    existing?: RenterFileListItem[];
    linked?: LinkedSurveyFileItem[];
    already_on_file?: OnFileDocument[];
    cross_member?: CrossMemberDocument[];
  }>(`${BASE}/uploads/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    signal,
    onUploadProgress: onProgress
      ? (event) => {
          const total = event.total ?? 0;
          const pct = total > 0 ? Math.round((event.loaded / total) * 100) : 0;
          onProgress(pct);
        }
      : undefined,
  });
  return {
    created: res.data.created ?? [],
    blocked: res.data.blocked ?? [],
    processing: res.data.processing ?? [],
    existing: res.data.existing ?? [],
    linked: res.data.linked ?? [],
    alreadyOnFile: res.data.already_on_file ?? [],
    crossMember: res.data.cross_member ?? [],
  };
}

/**
 * Reuse a document already on another member's survey of the same
 * certification (ENG-4086). Explicit renter action only; idempotent on the
 * server, so a double tap returns the existing file.
 */
export async function reuseCrossMemberFile({
  surveyId,
  sourceSurveyFileId,
  targetQitId,
  targetAnswerGroupId,
  signal,
}: {
  surveyId: number;
  sourceSurveyFileId: number;
  targetQitId: number;
  targetAnswerGroupId?: number;
  signal?: AbortSignal;
}): Promise<ReusedSurveyFile> {
  const res = await axiosInstance.post<{
    survey_file_id: number;
    original_filename: string;
    pdf_file_url: string;
    survey_answer_id: number | null;
    survey_file_status: number | null;
    survey_file_created_at: string | null;
  }>(
    `${BASE}/uploads/reuse-cross-member/`,
    {
      survey_id: surveyId,
      source_survey_file_id: sourceSurveyFileId,
      target_qit_id: targetQitId,
      ...(targetAnswerGroupId !== undefined
        ? { target_answer_group_id: targetAnswerGroupId }
        : {}),
    },
    { signal }
  );
  return res.data;
}

export async function deleteAIFile(
  fileId: number,
  signal?: AbortSignal
): Promise<void> {
  await axiosInstance.delete(`${BASE}/uploads/${fileId}/`, { signal });
}

export interface EscalateAIFileResult {
  escalated: boolean;
  already: boolean;
  event_id: number | null;
}

export async function escalateAIFile(
  fileId: number,
  signal?: AbortSignal
): Promise<EscalateAIFileResult> {
  const res = await axiosInstance.post<EscalateAIFileResult>(
    `${BASE}/uploads/${fileId}/escalate/`,
    undefined,
    { signal }
  );
  return res.data;
}

export async function listRenterFailedFiles(
  params: { survey?: number; certification?: number },
  signal?: AbortSignal
): Promise<RenterFileListItem[]> {
  const qs: Record<string, number> = {};
  if (params.survey !== undefined) {
    qs.survey = params.survey;
  }
  if (params.certification !== undefined) {
    qs.certification = params.certification;
  }
  const res = await axiosInstance.get<RenterFileListItem[]>(
    `${BASE}/uploads/failed/`,
    { params: qs, signal }
  );
  return res.data;
}

export type MemberConfirmationDecision = 'confirm' | 'reject' | 'delete';

export interface ResolveMemberConfirmationResult {
  status: string;
}

/**
 * POST /uploads/{id}/resolve-member-confirmation/ — submit the renter's
 * three-way decision for a cross-member mismatch pause (needs_member_
 * confirmation). Async dispatch — a 200 means the resume was queued, not
 * that it finished; the actual outcome (survey reassigned / kept / file
 * deleted) arrives later via WS (docs/plans/
 * sorting-hat-cross-member-and-unification.md §1).
 */
export async function resolveMemberConfirmation(
  fileId: number,
  decision: MemberConfirmationDecision,
  signal?: AbortSignal
): Promise<ResolveMemberConfirmationResult> {
  const res = await axiosInstance.post<ResolveMemberConfirmationResult>(
    `${BASE}/uploads/${fileId}/resolve-member-confirmation/`,
    { decision },
    { signal }
  );
  return res.data;
}

export async function patchMatch(
  fileId: number,
  matchId: number,
  patch: {
    value?: string;
    user_confirmed?: boolean;
    user_excluded?: boolean;
  },
  signal?: AbortSignal
): Promise<ExtractionMatch> {
  // T1-4: endpoint keyed by file_id + match_id
  const res = await axiosInstance.patch<ExtractionMatch>(
    `${BASE}/uploads/${fileId}/extracted-matches/${matchId}/`,
    patch,
    { signal }
  );
  return res.data;
}

export async function getSummary(
  surveyId: number,
  signal?: AbortSignal,
  targetAnswerGroupId?: number | null,
  targetQitId?: number | null
): Promise<SummaryResponse> {
  const params: Record<string, number> = { survey: surveyId };
  if (targetAnswerGroupId != null) {
    params.target_answer_group = targetAnswerGroupId;
  }
  if (targetQitId != null) {
    params.target_qit = targetQitId;
  }
  const res = await axiosInstance.get<SummaryResponse>(
    `${BASE}/uploads/summary/`,
    { params, signal }
  );
  if (!res.data.groups) {
    devWarn('getSummary', 'Response missing groups field');
  }
  return res.data;
}

export async function reshapeGroup(
  surveyId: number,
  groupKey: string,
  action: string,
  payload: Record<string, unknown>,
  signal?: AbortSignal
): Promise<{ status: string }> {
  const res = await axiosInstance.patch<{ status: string }>(
    `${BASE}/summary/${surveyId}/groups/${groupKey}/`,
    { action, ...payload },
    { signal }
  );
  return res.data;
}

export async function applyAll(
  surveyId: number,
  signal?: AbortSignal,
  targetAnswerGroupId?: number | null,
  targetQitId?: number | null
): Promise<ApplyResult> {
  const body: Record<string, number> = { survey_id: surveyId };
  if (targetAnswerGroupId != null) {
    body.target_answer_group_id = targetAnswerGroupId;
  }
  if (targetQitId != null) {
    body.target_qit_id = targetQitId;
  }
  const res = await axiosInstance.post<ApplyResult>(
    `${BASE}/uploads/summary/confirm/`,
    body,
    { signal }
  );
  if (!res.data.status) {
    devWarn('applyAll', 'Response missing status field');
  }
  return res.data;
}

export async function getDocSchemaCategories(
  signal?: AbortSignal
): Promise<DocCategoryWithSubTypes[]> {
  const res = await axiosInstance.get<DocCategoryWithSubTypes[]>(
    `${BASE}/doc-schema/categories/`,
    { signal }
  );
  return res.data;
}

export async function getDocSubTypes(
  categoryPk: number,
  signal?: AbortSignal
): Promise<DocSubType[]> {
  const res = await axiosInstance.get<DocSubType[]>(
    `${BASE}/doc-schema/categories/${categoryPk}/sub-types/`,
    { signal }
  );
  return res.data;
}

export async function getDocSchemaSteps(
  signal?: AbortSignal
): Promise<DocSchemaStep[]> {
  const res = await axiosInstance.get<DocSchemaStep[]>(
    `${BASE}/doc-schema/categories/steps/`,
    { signal }
  );
  return res.data;
}
